<template>
  <loading-page v-if="fetchingProviders" />
  <validation-observer
    v-else
    ref="rules"
    tag="div"
    class="h-100 position-relative"
  >
    <form-wrapper center>
      <template v-slot:body>
        <paper-simple v-if="externalAccount === null">
          <custom-input-radio
            v-model="selectedProvider"
            label="Choose an External Account provider"
            :options="options"
            disabled-item-class="disabled-radio-item"
            description-class="pl-0"
            class="custom-radio-gj"
            center
            second-border
            md-cols="3"
          />
        </paper-simple>

        <paper-simple>
          <h5 class="text-main mb-2">
            External Account information
          </h5>

          <validation-provider
            #default="{ errors }"
            name="Name*"
            rules="required|alpha-dash|min:3|max:50|"
          >
            <input-field
              label="Name*"
              label-position="top"
              :error="errors.length ? errors[0] : null"
            >
              <b-form-input
                v-model="account.username"
                :state="errors.length > 0 ? false:null"
                placeholder="External account unique name in Captain..."
                :disabled="externalAccount !== null"
              />
            </input-field>
          </validation-provider>

          <validation-provider
            #default="{ errors }"
            name="Label"
            rules="required|min:3|max:100"
          >
            <input-field
              label="Label*"
              label-position="top"
              :error="errors.length ? errors[0] : null"
            >
              <b-form-textarea
                id="input-description"
                v-model="account.label"
                placeholder="Enter a short description for the external account..."
                rows="2"
                no-resize
                :state="errors.length > 0 ? false : null"
              />
            </input-field>
          </validation-provider>

          <validation-provider
            v-if="!hasDetails"
            #default="{ errors }"
            name="Access Key"
            rules="required|min:16"
          >
            <input-field
              label="Access Key*"
              label-position="top"
              :error="errors.length ? errors[0] : null"
            >
              <b-form-input
                id="input-accessKey"
                v-model.trim="account.accessKey"
                type="text"
                placeholder="Access Key"
                required
                :state="errors.length > 0 ? false : null"
              />
            </input-field>
          </validation-provider>

          <validation-provider
            v-if="!hasDetails"
            #default="{ errors }"
            name="Secret Key"
            rules="required|min:16"
          >
            <input-field
              label="Secret Key*"
              label-position="top"
              :error="errors.length ? errors[0] : null"
            >
              <b-form-input
                id="input-secretKey"
                v-model.trim="account.secretKey"
                type="password"
                autocomplete="off"
                placeholder="Secret Key"
                required
                :state="errors.length > 0 ? false : null"
              />
            </input-field>
          </validation-provider>

          <validation-provider
            #default="{ errors }"
            name="Bucket Name"
            rules="min:2"
          >
            <input-field
              :info="messages.bucketName"
              label="Bucket Name"
              label-position="top"
              :error="errors.length ? errors[0] : null"
              @openInfo="showInfo(messages.bucketName.title, messages.bucketName.message)"
            >
              <b-form-input
                id="input-bucketName"
                v-model.trim="account.bucketName"
                placeholder="Bucket Name"
                required
                :state="errors.length > 0 ? false : null"
              />
            </input-field>
          </validation-provider>

          <validation-provider
            #default="{ errors }"
            name="Endpoint"
            rules="required|url"
          >
            <input-field
              label="Endpoint*"
              label-position="top"
              :error="errors.length ? errors[0] : null"
            >
              <b-form-input
                id="input-endpoint"
                v-model="account.endpoint"
                type="url"
                placeholder="Endpoint"
                required
                :state="errors.length > 0 ? false : null"
              />
            </input-field>
          </validation-provider>
        </paper-simple>
      </template>

      <template v-slot:footer>
        <b-button
          variant="bg-white"
          :to="{ name: 'external-accounts' }"
        >
          Cancel
        </b-button>
        <button-loading
          variant="bg-primary"
          class="ml-1"
          :spinning="addingExternalAccount"
          @click="addExternalAccount()"
        >
          Add
        </button-loading>
      </template>
    </form-wrapper>
    <info
      v-if="openInfo"
      :title="infoTitle"
      :message="infoMessage"
      @close="openInfo = false"
    />
  </validation-observer>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required, alphaNum, url } from '@validations'
import CustomInputRadio from '@/components/form/CustomInputRadio.vue'
import messages from './messages'

export default {
  name: 'AddExternalAccount',
  components: {
    CustomInputRadio,
    ValidationProvider,
    ValidationObserver,
  },
  props: {
    externalAccount: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      account: {
        username: '',
        label: '',
        accessKey: '',
        secretKey: '',
        bucketName: '',
        endpoint: 'https://',
      },
      infoTitle: '',
      infoMessage: '',
      openInfo: false,
      messages,
      required,
      alphaNum,
      url,
      selectedProvider: null,
    }
  },
  computed: {
    externalAccounts() {
      return this.$store.state.externalAccount.externalAccounts
    },
    providers() {
      return this.$store.state.externalAccount.providers
    },
    fetchingProviders() {
      return this.$store.state.externalAccount.fetchingProviders
    },
    options() {
      return this.providers.map(e => ({
        value: e.id,
        disabled: false,
        image: e.imageUrl,
        imageWidth: '65px',
        text: e.name,
      }))
    },
    hasDetails() {
      return !!this.externalAccount
    },
    addingExternalAccount() {
      return this.$store.state.externalAccount.creatingExternalAccount
    },
  },
  created() {
    this.fetchProviders()
    if (this.externalAccount) this.account = this.externalAccount
  },
  methods: {
    addExternalAccount() {
      this.$emit('save', {
        username: this.account.username,
        label: this.account.label,
        accessKey: this.account.accessKey,
        secretKey: this.account.secretKey,
        bucketName: this.account.bucketName,
        endpoint: this.account.endpoint,
        providerId: this.selectedProvider,
      })
    },
    showInfo(title, info) {
      this.openInfo = true
      this.infoTitle = title
      this.infoMessage = info
    },
    fetchProviders() {
      this.$store
        .dispatch('externalAccount/fetchProviders')
        .then(() => {
          this.selectedProvider = this.providers[0].id || null
        })
        .catch(error => {
          this.errorToast(
            error.response?.data?.errors
              || 'Something went wrong while fetching all external accounts.',
          )
        })
        .finally(() => {
          this.loading = false
        })
    },
  },

}
</script>
